import { useState, type FC } from "react";
import { useTranslation } from "react-i18next";

import type { BrandKitColor, BrandKitLogo } from "../server/brand-kit-service";
import type { BrandKitManager } from "../types";

import { BrandKitChoiceRow } from "./choice-row";
import { BrandKitColorPicker } from "./color-picker-dialog";
import { useDialog } from "../../../utils/hooks/use-dialog";

export const BrandKitColorChoiceRow: FC<{
  colors: BrandKitColor[];
  colorManager: BrandKitManager["color"];
  selected?: BrandKitColor;
  selectedText?: string;
  showTitle?: boolean;
  showDefaultIndicator?: boolean;
}> = ({
  colors,
  colorManager,
  selected,
  selectedText,
  showTitle = true,
  showDefaultIndicator = true,
}) => {
  const { t } = useTranslation();

  const dialog = useDialog();
  const [editingColor, setEditingColor] = useState<{ color: BrandKitColor; index: number } | null>(
    null,
  );

  const handleEdit = (color: BrandKitColor, index: number): void => {
    setEditingColor({ color, index });
    dialog.handleOpen();
  };

  const handleAddClick = (): void => {
    if (!editingColor) {
      setEditingColor(null);
      dialog.handleOpen();
    }
  };

  const handleEditSubmit = (updatedColor: BrandKitColor): void => {
    colorManager.edit?.(updatedColor);
    setEditingColor(null);
  };

  const handleDialogClose = (): void => {
    dialog.handleClose();
    setEditingColor(null);
  };

  return (
    <BrandKitChoiceRow
      title={showTitle ? t("Colors") : undefined}
      subtitle={t(
        "Manually add brand colors. These are used to generate customized images in Discover.",
      )}
      type="color"
      existing={colors ?? []}
      selected={selected}
      onClick={colorManager.add ? handleAddClick : undefined}
      remove={colorManager.remove}
      setSelected={colorManager.setSelected}
      selectedText={selectedText}
      showDefaultIndicator={showDefaultIndicator}
      addLabel={t("Add Color")}
      onEdit={
        colorManager.edit
          ? (item: BrandKitLogo | BrandKitColor, index: number) => {
              if ("hex" in item) {
                // Type guard to check if it's a BrandKitColor
                handleEdit(item, index);
              }
            }
          : undefined
      }
    >
      {colorManager.add && (
        <BrandKitColorPicker
          open={dialog.open}
          handleClose={handleDialogClose}
          onChange={colorManager.add}
          existingColor={editingColor?.color || undefined}
          onEdit={editingColor ? handleEditSubmit : undefined}
          onDelete={() =>
            colorManager.remove ? colorManager.remove(editingColor?.index || 0) : undefined
          }
        />
      )}
    </BrandKitChoiceRow>
  );
};
